/** Assign a single vehicle to a route (per-row action, cross-tenant).
 *
 * Routes are scoped to the vehicle's own tenant. The currently-assigned route is
 * preselected so the dialog doubles as a re-assign.
 */
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as fleetVehiclesApi from '@/api/fleetVehicles';
import type { Vehicle } from '@/api/fleetVehicles';
import { useSnackbar } from '@/components/Snackbar';

import { DEFAULT_MARKER_COLOR } from './shared';

interface Props {
  open: boolean;
  vehicle: Vehicle | null;
  onClose: () => void;
  onAssigned: () => void;
}

export function AssignRouteDialog({ open, vehicle, onClose, onAssigned }: Props) {
  const snackbar = useSnackbar();
  const [routeId, setRouteId] = useState<number | ''>('');
  const toId = vehicle?.tenant?.id ?? null;

  const routesQuery = useQuery({
    queryKey: ['fleet-vehicle-routes', toId],
    queryFn: () => fleetVehiclesApi.listRoutes(toId as number),
    enabled: open && toId != null,
  });

  useEffect(() => {
    if (open) setRouteId(vehicle?.route?.id ?? '');
  }, [open, vehicle?.route?.id]);

  const assignMutation = useMutation({
    mutationFn: (rid: number) => fleetVehiclesApi.assignRoute(vehicle!.id, rid),
    onSuccess: () => {
      snackbar.show('Route assigned.', 'success');
      onAssigned();
      onClose();
    },
    onError: (e: unknown) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Could not assign the route.', 'error'),
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Assign route{vehicle ? ` — ${vehicle.name}` : ''}</DialogTitle>
      <DialogContent>
        <TextField
          select fullWidth size="small" label="Route" value={routeId} sx={{ mt: 1 }}
          onChange={(e) => setRouteId(e.target.value === '' ? '' : Number(e.target.value))}
        >
          {(routesQuery.data?.results ?? []).map((r) => (
            <MenuItem key={r.id} value={r.id}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: r.color || DEFAULT_MARKER_COLOR, flexShrink: 0 }} />
                <span>{r.name}{!r.enabled ? ' (disabled)' : ''}</span>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={routeId === '' || assignMutation.isPending}
          onClick={() => routeId !== '' && assignMutation.mutate(routeId)}
        >
          {assignMutation.isPending ? 'Assigning…' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
